<template>
  <b-modal
    id="genericFilterModal"
    size="lg"
    :title="`Filter: ${filter_layer}`"
    content-class="filter-modal-content"
    header-class="filter-modal-header"
    body-class="filter-modal-body"
    hide-footer
    @hidden="closed"
  >
    <!-- Filter rule builder -->
    <div class="filter-builder">
      <span class="filter-builder-title">Add a filter rule</span>
      <b-row class="align-items-end no-gutters">
        <b-col class="col-sm-4 pr-2">
          <label class="filter-field-label">Property</label>
          <b-form-select
            v-model="filter.property"
            class="form-control"
            :class="missingFields.includes('property') ? 'red-border': ''"
            @change="checkProperty"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                Select Property
              </b-form-select-option>
            </template>
            <b-form-select-option
              :key="i"
              :value="obj"
              v-for="(obj, i) in filterValues"
            >{{ obj.property }}</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col class="col-sm-2 pr-2">
          <label class="filter-field-label">Operator</label>
          <b-form-select
            v-model="filter.operator"
            @change="checkOperator"
            class="form-control"
            :class="missingFields.includes('operator') ? 'red-border': ''"
            :disabled="filter.property && filter.property?.filtervalue?.length > 0"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                Operator
              </b-form-select-option>
            </template>
            <b-form-select-option
              v-for="op in operators"
              :key="op.id"
              :value="op.tag"
            >
              {{ op.tag }}
            </b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col class="col-sm-2 pr-2" v-if="filter.property && filter.property.filtervalue">
          <label class="filter-field-label">Value</label>
          <b-form-select
            v-model="filter.value_1"
            class="form-control"
            :class="missingFields.includes('value_1') ? 'red-border': ''"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                Select Filter Value
              </b-form-select-option>
            </template>
            <b-form-select-option
              :key="i"
              :value="value"
              v-for="(value, i) in filter.property.filtervalue"
            >{{ value }}</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col class="col-sm-2 pr-2" v-else>
          <label class="filter-field-label">Value</label>
          <b-form-input
            v-model="filter.value_1"
            class="form-control"
            :class="missingFields.includes('value_1') ? 'red-border': ''"
            placeholder="Value"
          ></b-form-input>
        </b-col>
        <b-col class="col-sm-2 pr-2 filter_value_2">
          <label
            class="filter-field-label"
            :class="{ 'invisible': isHidden || !(filter && filter.property) }"
          >Value 2</label>
          <b-form-input
            v-model="filter.value_2"
            class="form-control"
            :class="missingFields.includes('value_2') ? 'red-border': ''"
            placeholder="Value"
            :hidden="isHidden || !(filter && filter.property)"
          ></b-form-input>
        </b-col>
        <b-col class="col-sm-2 filter-actions">
          <b-button
            class="filter-btn filter-add-btn"
            @click="addRule()"
          >
            Apply Filter
          </b-button>
         
        </b-col>
      </b-row>
    </div>

    <!-- Displaying added filter rules as chips -->
    <div
      v-if="rules.filter(r => r.layer == filter_layer).length"
      class="active-rules"
    >
      <span class="active-rules-title">Active rules</span>
      <div class="rule-chips">
        <span
          class="rule-chip"
          v-for="(rule, index) in rules.filter(r => r.layer == filter_layer)"
          :key="index"
        >
          <span class="rule-chip-prop">{{ rule.property }}</span>
          <span class="rule-chip-op">{{ rule.operator }}</span>
          <span class="rule-chip-val">
            {{ rule.value_1 }}<template v-if="rule.value_2 != null"> &ndash; {{ rule.value_2 }}</template>
          </span>
          <fa :icon="['fas', 'times']" class="rule-chip-remove" @click="removeRule(index)" />
        </span>
      </div>
    </div>

    <!-- Options + hint -->
    <div class="filter-options">
      <p class="filter-hint">
        <fa :icon="['fas', 'info-circle']" class="mr-1" />
        Use dropdowns to create a filter for the layer. Press <strong>Add Rule</strong> to add it to the layer.
      </p>
       <b-button
            v-if="rules.filter(r => r.layer == filter_layer).length"
            variant="outline-danger"
            class="filter-btn filter-clear-btn"
            @click="removeFilter"
          >
            Clear All Filters
          </b-button>
    </div>
  </b-modal>
</template>

<style src="./Generic.css" scoped></style>

<style>
#genericFilterModal .modal-lg {
  max-width: 68.75rem !important;
}

/* ---- Modal shell ---- */
#genericFilterModal .filter-modal-content {
  overflow: hidden;
  font-family: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

#genericFilterModal .filter-modal-header {
  background: #063c6d !important;
  color: #fff !important;
  padding: 1rem 1.4rem;
  border-bottom: none;
}

#genericFilterModal .filter-modal-header .modal-title {
  font-weight: 600;
  font-size: 1.15rem;
  letter-spacing: 0.2px;
  text-transform: capitalize;
}

#genericFilterModal .filter-modal-header .close {
  color: #fff;
  opacity: 0.8;
  text-shadow: none;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

#genericFilterModal .filter-modal-header .close:hover {
  opacity: 1;
  transform: scale(1.1);
}

#genericFilterModal .filter-modal-body {
  padding: 1.3rem 1.4rem 1.4rem;
  color: #063c6d;
}

/* ---- Rule builder card ---- */
#genericFilterModal .filter-builder {
  background: #f4f6f8;
  border: 1px solid rgba(6, 60, 109, 0.1);
  border-radius: 12px;
  padding: 0.9rem 1rem 1rem;
  margin-bottom: 1.1rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7),
    0 6px 18px rgba(6, 60, 109, 0.08);
}

#genericFilterModal .filter-builder-title {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: rgba(6, 60, 109, 0.55);
  margin-bottom: 0.7rem;
}

#genericFilterModal .filter-field-label {
  display: block;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.4px;
  color: rgba(6, 60, 109, 0.6);
  margin-bottom: 0.25rem;
}

/* ---- Form controls ---- */
#genericFilterModal .form-control,
#genericFilterModal .custom-select {
  background-color: #f4f6f8;
  border: 1px solid rgba(6, 60, 109, 0.15);
  border-radius: 8px;
  color: #063c6d;
  font-size: 0.88rem;
  box-shadow: inset 0 1px 2px rgba(6, 60, 109, 0.06);
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;
  padding: 0.375rem 1.75rem 0.375rem 0.75rem !important;
}

#genericFilterModal .form-control::placeholder {
  color: #8aa6bf;
}

#genericFilterModal .form-control:hover,
#genericFilterModal .custom-select:hover {
  border-color: rgba(74, 159, 224, 0.6);
  background-color: #fff;
}

#genericFilterModal .form-control:focus,
#genericFilterModal .custom-select:focus {
  border-color: #4a9fe0;
  box-shadow: 0 0 0 3px rgba(74, 159, 224, 0.2);
  background-color: #fff;
}

#genericFilterModal select.form-control {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3e%3cpath fill='%230a4a7f' d='M0 1.5 4 5.5 8 1.5z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.6rem center;
  background-size: 9px;
}

#genericFilterModal .red-border {
  border-color: #e5484d !important;
  background-color: #fdeceb !important;
}

/* ---- Active rules as chips ---- */
#genericFilterModal .active-rules {
  margin-bottom: 1.1rem;
}

#genericFilterModal .active-rules-title {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: rgba(6, 60, 109, 0.55);
  margin-bottom: 0.55rem;
}

#genericFilterModal .rule-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

#genericFilterModal .rule-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.7rem;
  background: rgba(6, 60, 109, 0.06);
  border: 1px solid rgba(6, 60, 109, 0.15);
  border-radius: 999px;
  font-size: 0.82rem;
}

#genericFilterModal .rule-chip-prop {
  font-weight: 600;
  color: #063c6d;
}

#genericFilterModal .rule-chip-op {
  color: rgba(6, 60, 109, 0.6);
  font-weight: 500;
}

#genericFilterModal .rule-chip-val {
  color: #063c6d;
  font-weight: 500;
}

#genericFilterModal .rule-chip-remove {
  cursor: pointer;
  color: rgba(6, 60, 109, 0.45);
  margin-left: 0.15rem;
  transition: color 0.15s ease, transform 0.1s ease;
}

#genericFilterModal .rule-chip-remove:hover {
  color: #e5484d;
  transform: scale(1.15);
}

/* ---- Options + hint ---- */
#genericFilterModal .filter-options {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.2rem;
  padding: 0.9rem 0;
  border-top: 1px solid rgba(6, 60, 109, 0.1);
  margin-bottom: 0.5rem;
}

#genericFilterModal .filter-hint {
  flex: 1 1 auto;
  font-size: 0.78rem;
  line-height: 1.45;
  color: rgba(6, 60, 109, 0.65);
  margin: 0;
  padding: 0.6rem 0.8rem;
  background: rgba(74, 159, 224, 0.1);
  border-left: 3px solid #4a9fe0;
  border-radius: 0 8px 8px 0;
}

#genericFilterModal .filter-hint .svg-inline--fa {
  color: #0a5499;
}

#genericFilterModal .filter-hint strong {
  color: #063c6d;
  font-weight: 700;
}

/* ---- Footer ---- */
#genericFilterModal .filter-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  padding-top: 0.4rem;
}

/* ---- Buttons ---- */
#genericFilterModal .filter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.86rem;
  letter-spacing: 0.2px;
  padding: 0.5rem 1.15rem;
  border: none;
  text-shadow: none;
  transition: transform 0.12s ease, box-shadow 0.15s ease, filter 0.15s ease,
    background 0.15s ease, color 0.15s ease;
}

#genericFilterModal .filter-btn:hover {
  transform: translateY(-1px);
  filter: brightness(1.05);
  box-shadow: 0 4px 12px rgba(6, 60, 109, 0.2);
}

#genericFilterModal .filter-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

/* Inline action buttons in the builder row */
#genericFilterModal .filter-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
/* Add Rule — primary accent */
#genericFilterModal .filter-add-btn {
  background: linear-gradient(135deg, #038018, #056e28);
  box-shadow: 0 2px 8px rgba(47, 196, 79, 0.4);
}
#genericFilterModal .filter-clear-btn {
  background: linear-gradient(135deg, #a0070f, #ac0513);
  box-shadow: 0 2px 8px rgba(196, 47, 47, 0.4);
  color: white;
}

#genericFilterModal .filter-close-btn:hover {
  background: #063c6d;
  border-color: #063c6d !important;
  color: #fff;
}
</style>

<script>
import axios from 'axios'
import GeoJSON from 'ol/format/GeoJSON'
import VectorSource from 'ol/source/Vector'
import {get as projGet, transformExtent} from 'ol/proj'
export default {
  name: "Generic_LayerFilterModal",
  props: [
    'layerProperties',
    'filter_layer',
    'mmp_layersObj',
    'zoomLevelCap',
    'generateCQLObj',
    'map',
    'selectedElement'
  ],
  data() {
    return {
      isHidden: true,
      filter: {
        property: null,
        operator: null,
        value_1: null,
        value_2: null
      },
      operators: [
        {id: 1, tag: '<'},
        {id: 2, tag: '>'},
        {id: 3, tag: '='},
        {id: 4, tag: '<>'},
        {id: 5, tag: '<='},
        {id: 6, tag: '>='},
        {id: 7, tag: 'BETWEEN'}
      ],
      filterValues: [],
      valueInput: "",
      missingFields: [],
      rules: [],
      maintainView: false,
      maintainViewArr: []
    }
  },
  methods: {
    checkProperty() {
      if (this.filter.property && this.filter.property.filtervalue) {
        this.filter.operator = '=';
      } else {
        this.filter.operator = null;
      }
    },
    async generateCQL(filterLayer = null, enableZoom = false) {
      let layerName = (filterLayer ? filterLayer : this.filter_layer)
        .split('_')
        .map(word => word[0].toUpperCase() + word.substring(1))
        .join(' ')

      if (this.mmp_layersObj[layerName] || this.mmp_layersObj[this.filter_layer]) {
        if (this.mmp_layersObj[layerName]) {
          filterLayer = layerName
        } else {
          filterLayer = this.filter_layer
        }

        let layerSource = this.mmp_layersObj[filterLayer].layer.getSource()
        let layerFilter = ''
        let currentLayer
        this.rules
          .filter(r => r.layer == filterLayer || r.layer == this.filter_layer)
          .forEach(i => {
            currentLayer = ''

            if (i.layer == "delivery_coverage" && (i.property == 'left_neighbors' || i.property == 'right_neighbors')) {
              if (i.value_1.indexOf(',') > -1) {
                let values = i.value_1.split(',')
                i.value_1 = values.join('%')
              }

              currentLayer = (i.operator = '=')
                ? `${i.property} like '%${i.value_1}%'`
                : !i.value_2
                  ? `${i.property} ${i.operator} '${i.value_1}'`
                  : `${i.property} >= '${i.value_1}' AND ${i.property} <= '${i.value_2}'`
            } else {
              currentLayer = !i.value_2
                ? `${i.property} ${i.operator} '${i.value_1}'`
                : `${i.property} >= '${i.value_1}' AND ${i.property} <= '${i.value_2}'`
            }

            layerFilter += (layerFilter == '' ? '' : ' AND ') + currentLayer

            layerSource.updateParams({
              CQL_FILTER: layerFilter
            })
          })

        if (enableZoom) {
          let index = this.maintainViewArr.findIndex(
            obj => obj.layer == filterLayer
          )

          if (index == -1) {
            // Filtered layer not found in maintainViewArr, add to array
            this.maintainViewArr.push({
              layer: filterLayer,
              viewMaintained: this.maintainView
            })
          } else {
            // Filtered layer found in maintainViewArr
            let obj = this.maintainViewArr[index]
            obj.viewMaintained = this.maintainView
          }

          /* If Maintain View checkbox isn't checked and APPLY button
          in filter modal is clicked, zoom to bound all features */
          this.zoomToGeoServerLayer(
            layerName,
            filterLayer,
            layerFilter,
            !this.maintainView && enableZoom
          )
        }

        this.$emit('rulesChanged', this.rules)

        this.$bvModal.hide('filterModal')
      }
    },
    closeModal() {
      this.$bvModal.hide('genericFilterModal')
      this.closed();
    },
    closed() {
      let elementFilterIcon = document.getElementById(this.selectedElement)
      let elementLayerName = elementFilterIcon.parentNode.parentNode.id
      let noRulesForElement =
        this.rules.filter(r => r.layer == elementLayerName).length == 0
      let index = parseInt(this.selectedElement.split('icon')[1])
      let passedClass = {hover: true}

      if (noRulesForElement) {
        elementFilterIcon.classList.remove('orange-font')
      }

      this.$emit('filterClassesObjChanged', {
        index: index,
        passedClass: passedClass
      })

      this.reset()
    },
    checkOperator() {
      this.isHidden = this.filter.operator != 'BETWEEN'
    },
    removeRule(index) {
      this.rules.splice(index, 1)

      if (this.rules.length == 0) {
        this.removeFilter();
      } else {
        this.generateCQL(null, true)
      }

      // this.mmp_layersObj[this.filter_layer].layer.getSource().updateParams({
      //   CQL_FILTER: null
      // })
      // this.$emit('rulesChanged', this.rules)
      // this.generateCQL(null, true)
    },
    removeFilter() {
      this.rules = this.rules.filter(r => r.layer != this.filter_layer)
      this.mmp_layersObj[this.filter_layer].layer.getSource().updateParams({
        CQL_FILTER: null
      })
      this.$emit('rulesChanged', this.rules)
    },
    reset() {
      this.filter = {
        property: null,
        operator: null,
        value_1: null,
        value_2: null
      }
      this.isHidden = true
      this.missingFields = []
    },
    validateInputs() {
      if (this.filter.property == null) {
        this.missingFields.push('property')
      }
      if (this.filter.operator == null) {
        this.missingFields.push('operator')
      }
      if (this.filter.value_1 == null) {
        this.missingFields.push('value_1')
      }
      if (!this.isHidden && this.filter.value_2 == null) {
        this.missingFields.push('value_2')
      }
      setTimeout(() => {
        this.missingFields = []
      }, 5000)
    },
    async addRule() {
      await this.validateInputs();
      if (!this.missingFields.length) {
        this.rules.push({
          layer: this.filter_layer,
          property: this.filter.property.property,
          operator: this.filter.operator,
          value_1: this.filter.value_1,
          value_2: this.filter.value_2
        })
        this.reset()
        this.generateCQL(null, true)
      }
    },
    zoomToGeoServerLayer(layerName, origLayerName, filter, enableZoom) {
      let layerSource = this.mmp_layersObj[origLayerName].layer.getSource()
      let layerType = layerSource.getParams().LAYERS
      let validFilter = ![null, undefined, ''].includes(filter)
      let url =
        layerSource.getUrls()[0] +
        '?service=WFS&version=1.0.0&request=GetFeature&typeName=' +
        layerType +
        '&outputFormat=application/json&format_options=callback:getJsonFeatureExtent&authkey=' + sessionStorage.token + '&CQL_FILTER=' +
        encodeURI(filter)

      if (sessionStorage.token && typeof sessionStorage.token == "string" && validFilter) {
        axios
          .get(url, {})
          .then(response => {
            let features = new GeoJSON().readFeatures(response.data)
            if (features.length != 0) {
              if (enableZoom) {
                if (!layerType.includes('_hd')) {
                  let minZoom = this.mmp_layersObj[this.filter_layer].minZoom
                  let zoomControl = this.mmp_layersObj[this.filter_layer].zoomControl
                  let zoomValue = features.length == 1 ? this.zoomLevelCap : !zoomControl ? 3 : minZoom ? minZoom : this.zoomLevelCap
                  this.map
                    .getView()
                    .fit(
                      new transformExtent(
                        new VectorSource({features: features.slice(0, 1)}).getExtent(),
                        new projGet('EPSG:4326'),
                        new projGet('EPSG:3857')
                      ),
                      {
                        maxZoom: zoomValue
                      }
                    )
                }
              }
            } else {
              this.$bvToast.toast(
                `No features found with these filters on ${layerName} layer.`,
                {title: 'No Features Found'}
              )
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      }
    },
    getFilters() {
      if (!this.filter_layer) {
        return;
      }
      this.filterValues = []
      if (sessionStorage.token) {
        axios.get(process.env.API_LOCATION + '/api/layer/getLayerFilters/', {
          method: 'GET',
          headers: {
            'x-auth-token': sessionStorage.token
          },
          params: {
            layerName: this.filter_layer
          }
        })
        .then(response => {
          const content = response.data.rows
          this.reset();
          this.filterValues = content;
        })
        .catch(function (error) {
          console.log(error)
        })
      } else {
          console.log('No User Token')
      }
    }
  },
  watch: {
    generateCQLObj: function (newValue, oldValue) {
      this.generateCQL(newValue.layer, newValue.enableZoom)
    },
    filter_layer: function (newValue) {
      if (newValue) {
        this.getFilters()
      }
    }
  }
}
</script>
